import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, RefreshCw, Filter } from "lucide-react";

import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import MISFilterDrawer from "../MISFilterDrawer";


const COLORS = ["#EF4444", "#F97316", "#EAB308", "#DC2626"];

const LeadLeakageSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm animate-pulse">
      <div className="p-5 border-b">
        <div className="h-6 w-40 bg-gray-200 rounded" />
      </div>

      <div className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-24 bg-gray-100 rounded-xl" />
          ))}
        </div>

        <div className="h-[260px] bg-gray-100 rounded-xl" />
      </div>
    </div>
  );
};

const LeadLeakageError = ({ onRetry }) => {
  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
      <h3 className="font-semibold text-red-600 mb-2">
        Failed to load Lead Leakage Report
      </h3>

      <button
        onClick={onRetry}
        className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg"
      >
        Retry
      </button>
    </div>
  );
};

const LeadLeakageCard = () => {
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState(null);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const [error, setError] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${SummaryApi.getLeadLeakageReport.url}?${params.toString()}`,
      );

      if (response?.data?.success) {
        setData(response.data.data);
      }
    } catch (error) {
      setError(true);
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) {
    return <LeadLeakageSkeleton />;
  }

  if (error) {
    return <LeadLeakageError onRetry={fetchReport} />;
  }
  return (
    <>
      {/* <div className="bg-white rounded-2xl border border-gray-200 shadow-sm"> */}
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.4,
        }}
        viewport={{
          once: true,
        }}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm"
      >
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-red-600" size={18} />

            <h2 className="font-semibold text-lg">Lead Leakage</h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchReport}
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>

            <button
              onClick={() => setShowFilters(true)}
              className="h-9 w-9 rounded-lg bg-red-600 text-white flex items-center justify-center"
            >
              <Filter size={15} />
            </button>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div className="rounded-xl bg-red-50 p-4">
              <p className="text-sm text-gray-500">No Followup</p>
              <h3 className="text-2xl font-bold text-red-600">
                {data.summary.noFollowup}
              </h3>
            </div>

            <div className="rounded-xl bg-orange-50 p-4">
              <p className="text-sm text-gray-500">Overdue</p>
              <h3 className="text-2xl font-bold text-orange-600">
                {data.summary.overdueFollowup}
              </h3>
            </div>

            <div className="rounded-xl bg-yellow-50 p-4">
              <p className="text-sm text-gray-500">Stuck Leads</p>
              <h3 className="text-2xl font-bold text-yellow-600">
                {data.summary.stuckLeads}
              </h3>
            </div>

            <div className="rounded-xl bg-rose-50 p-4">
              <p className="text-sm text-gray-500">Lost Leads</p>
              <h3 className="text-2xl font-bold text-rose-600">
                {data.summary.lostLeads}
              </h3>
            </div>
          </div>
          <div className="mt-5 flex justify-between items-center rounded-xl border border-red-200 bg-red-50 p-4">
            <div>
              <p className="text-sm text-gray-500">Highest Risk Area</p>

              <h4 className="font-semibold text-red-600">Overdue Followups</h4>
            </div>

            <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
              High Risk
            </span>
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.chart}
                  dataKey="count"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={4}
                  animationDuration={1200}
                >
                  {data.chart.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>

                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>

      <MISFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={() => {
          setAppliedFilters(filters);

          setShowFilters(false);
        }}
      />
    </>
  );
};

export default LeadLeakageCard;
